import React, { Fragment, useState } from 'react';
import requiredIf from 'react-required-if';
import PropTypes from 'prop-types';
import Card from '@mui/material/Card';
import axios from 'axios';
import _ from 'lodash';

import { useStyles } from '~/assets/styles';
import ContactMiniCard from '~/components/Contacts/ContactMiniCard';
import { EditContactDialog } from '~/components/Contacts/UpsertContact/Edit/EditContactDialog';
import { PERMISSION_ACTIONS, PERMISSION_VERBS } from '~/components/core';
import CircularProgress from '~/components/core/Atomic/CircularProgress/CircularProgress';
import { useRestrictedPermissions } from '~/components/core/Permissions/RestrictedPermissions';
import { useHasPermission } from '~/components/hooks/useHasPermission';
import Popover from '~/components/ui/atoms/Popover';
import usePopoverState from '~/components/ui/atoms/Popover/usePopoverState';
import { reportAxiosError, reportErrorInProductionOrThrow } from '~/Utils';
import cn from '~/Utils/cn';

import { contactPopulate } from './Contacts/ContactUtils';
import PermissionsButtonWrapper from './core/Permissions/PermissionsButtonWrapper';
import { useCms } from './hooks/useCms';
import { useClaim } from './ClaimContainer';
import { ContactIcon } from './icons';
import OverflowTextWithToolTip from './OverflowTextWithToolTip';

// PermissionsButtonWrapper disables its child when the user may not read contacts, so the button
// and its icon both take `disabled`. `iconDisabled` only greys the icon. `inline-flex` keeps the
// button as tall as the icon: as an inline box it would add a text line's height below it.
const ContactButton = ({ disabled, iconDisabled, iconClassName, ...buttonProps }) => (
  <button type="button" className="button-reset inline-flex" disabled={disabled} {...buttonProps}>
    <ContactIcon className={iconClassName} disabled={disabled || iconDisabled} />
  </button>
);

ContactButton.propTypes = {
  disabled: PropTypes.bool,
  iconDisabled: PropTypes.bool,
  iconClassName: PropTypes.string,
};

function ContactEntity(props) {
  const {
    contactId,
    contactDisplayName,
    hideDisplayName,
    onContactUpdate,
    maxWidth,
    shallRefetchOnContactIdChange = false,
    claimId,
  } = props;

  const { triggerProps, popoverProps, openFrom, close: closePopover } = usePopoverState();
  const [editContactOpen, setEditContactOpen] = useState(undefined);

  const [contact, setContact] = useState(undefined);

  const { claim, onAsyncClaimUpdate } = useClaim();
  const { user, userOrganization } = useCms();

  const { classes } = useStyles();

  const { userHasContextPermissions } = useRestrictedPermissions();
  const hasContactReadPermission = useHasPermission({
    action: PERMISSION_ACTIONS.CONTACT,
    verb: PERMISSION_VERBS.READ,
  });

  const handleButtonClick = (e) => {
    e.preventDefault();

    if (!hasContactReadPermission) {
      return;
    }

    openFrom(e);
    fetchContactDetails();
  };

  const updateClaimIfPossible = () => {
    if (onAsyncClaimUpdate) {
      onAsyncClaimUpdate();
    }
  };

  const handleContactUpdate = async () => {
    updateClaimIfPossible();

    const contact = await fetchContactDetails();
    // contact === undefined if fetchContactDetails failed fetching
    if (onContactUpdate && contact !== undefined) {
      await onContactUpdate(contact);
    }
  };

  const fetchContactDetails = async () => {
    try {
      const contact = await getContact(contactId, claim, userOrganization.id);
      // getContact returns undefined instead of throwing when it refuses to fetch (missing
      // contactId or orgId). Leave the contact already on screen alone rather than clearing it.
      if (contact === undefined) {
        return undefined;
      }
      setContact(contact);
      return contact;
    } catch (error) {
      reportAxiosError(error);
    }
  };

  // Without CONTACT:READ these background fetches 403 and surface the global permission
  // dialog on every screen that mounts this component.
  React.useEffect(() => {
    // shallRefetchOnContactIdChange consumers are covered by the effect below, which fires
    // on the same renders and fetches unconditionally; running both duplicates the request.
    if (hasContactReadPermission && !shallRefetchOnContactIdChange && !contact && !contactDisplayName) {
      fetchContactDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasContactReadPermission]);

  React.useEffect(() => {
    hasContactReadPermission && shallRefetchOnContactIdChange && fetchContactDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId, hasContactReadPermission]);

  const contactText = contact ? contact.full_name : contactDisplayName;
  const contactDetailsLabel = contactText ? `Contact details for ${contactText}` : 'Contact details';

  return (
    <Fragment>
      <span className={classes.containerCentered}>
        <PermissionsButtonWrapper action={PERMISSION_ACTIONS.CONTACT} verb={PERMISSION_VERBS.READ}>
          <ContactButton
            {...triggerProps}
            onClick={handleButtonClick}
            aria-label={contactDetailsLabel}
            iconClassName={cn(classes.leftButtonIcon, 'shrink-0', {
              [classes.hoverableIcon]: userHasContextPermissions,
              [classes.hoverableIconDisabled]: !userHasContextPermissions,
            })}
            iconDisabled={user.role.is_view_only && !claim}
          />
        </PermissionsButtonWrapper>
        <OverflowTextWithToolTip maxWidth={maxWidth ? maxWidth : '150px'}>
          {!hideDisplayName && contactText}
        </OverflowTextWithToolTip>
      </span>
      <Popover {...popoverProps} aria-label={contactDetailsLabel}>
        {!contact ? (
          <Card>
            <CircularProgress />
          </Card>
        ) : (
          <ContactMiniCard
            contact={contact}
            onEdit={() => {
              closePopover();
              setEditContactOpen(true);
            }}
            onClose={closePopover}
            onUpdate={handleContactUpdate}
            claimId={claimId}
          />
        )}
      </Popover>
      {
        // the following dialog can only be called from the ContactMiniCard which is rendered only after fetchContactDetails returns
      }
      {contact && editContactOpen && (
        <EditContactDialog
          contact={contact}
          onClose={() => setEditContactOpen(false)}
          onContactUpdate={handleContactUpdate}
        />
      )}
    </Fragment>
  );
}

ContactEntity.propTypes = {
  contactId: PropTypes.number.isRequired,
  hideDisplayName: PropTypes.bool,
  contactDisplayName: requiredIf(PropTypes.string, (props) => !props.hideDisplayName),
  onContactUpdate: PropTypes.func,
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  claimId: PropTypes.number,
  shallRefetchOnContactIdChange: PropTypes.bool,
};

async function getContact(contactId, claim = null, orgId = null) {
  if (contactId === undefined || contactId === null) {
    reportErrorInProductionOrThrow(`getContact: contactId is required (orgId ${orgId})`);
    return undefined;
  }
  const isClaimContact = claim && claim.contacts.map((contact) => contact.id).includes(contactId);
  if (!orgId) {
    reportErrorInProductionOrThrow(`getContact: orgId is required for contact ${contactId}`);
    return undefined;
  }
  const result = await axios.get(
    isClaimContact
      ? `/api/v1/organizations/${orgId}/claim_contacts/claims/${claim.id}/contacts/${contactId}`
      : `/api/v1/organizations/${orgId}/contacts/${contactId}`
  );
  const contact = contactPopulate(result.data);

  return contact;
}

function getSupportedContactRolesTypes(organizationRolesDict, initialRolesList, claimTypesList) {
  let rolesList = [...initialRolesList];
  rolesList = rolesList.filter(
    (roleType) =>
      organizationRolesDict[roleType] &&
      (organizationRolesDict[roleType].lobs.length > 0
        ? _.intersection(organizationRolesDict[roleType].lobs, claimTypesList).length !== 0
        : true)
  );

  rolesList = rolesList.filter(
    (roleType) =>
      organizationRolesDict[roleType] &&
      (organizationRolesDict[roleType].excluded_claim_types
        ? _.difference(claimTypesList, organizationRolesDict[roleType].excluded_claim_types).length !== 0
        : true)
  );

  return rolesList;
}

export { ContactEntity, getContact, getSupportedContactRolesTypes };
